import Bugsnag from '@bugsnag/js';
import { sha256 } from 'js-sha256';

import { CurrencyCode } from '@/__generated__/storefront/graphql';
import { contextualError } from '@/shared/utils/error';

type FeatureName =
  | 'pattern_product_suggestions'
  | 'product_review'
  | 'yarn_patterns';

export type UserEventDefinitions = {
  'show popup': {
    category: string;
    label: string;
  };
  'close popup': {
    category: string;
    label: string;
  };
  'clicked on already a subscriber': {
    category: string;
    label: string;
  };
  'clicked on signup': {
    category: string;
    label: string;
  };
  pattern_download_signup: {
    category: string;
    customer_name_hash?: string;
    customer_email_hash?: string;
    currency: CurrencyCode;
  };
  pattern_products_add_to_cart: {
    category: string;
    label?: string;
    currency: CurrencyCode;
    items: {
      item_id: string;
      item_name?: string;
      item_category?: string;
      item_variant?: string;
      price: number;
      quantity?: number;
    }[];
    value: number;
  };
  pattern_products_add_all_to_cart: {
    category: 'all_recommendations';
    label?: string;
    currency: CurrencyCode;
    items: {
      item_id: string;
      item_name?: string;
      item_category?: string;
      item_variant?: string;
      price: number;
      quantity?: number;
    }[];
    value: number;
  };
  feature_view: {
    category: 'section' | 'modal';
    item_name: FeatureName;
    label: string;
  };
  add_review: {
    category: 'Product Review';
    item_id: string;
    item_name: string;
    label: string;
    value: number;
  };
  show_original_review: {
    category: 'Product Review';
    item_id: string;
    item_name: string;
    label: string;
    value: number;
  };
  page_view: {
    page_title: string;
  };
};

type EventNames = keyof UserEventDefinitions;

type TrackHobbiiEvent = <T extends EventNames>(
  event: T,
  data: UserEventDefinitions[T]
) => void;

type DownloadPatternData = {
  action: 'download_pattern';
  category: 'plus_pattern' | 'free_pattern';
  label: string;
  item_id: string | number;
};

// only event that is tracked with another event name than hobbii_event
export const trackDownloadPattern = (data: DownloadPatternData) => {
  window.dataLayer?.push({
    event: 'download_pattern',
    user_event_data: data
  });
};

export const trackHobbiiEvent: TrackHobbiiEvent = (eventName, data) => {
  try {
    window.Shopify.analytics.publish('hobbii_event', {
      // We need to use hobbii_event for all new events except download_pattern, to avoid configuring GTM for every new type of event
      eventName,
      data
    });
  } catch (error) {
    const errorWithContext = contextualError('trackHobbiiEvent', error, {
      data
    });
    if (errorWithContext && Bugsnag.isStarted()) {
      Bugsnag.notify(errorWithContext, (event) => {
        event.addMetadata('tracking', {
          eventName,
          data
        });
      });
    }
  }
};

// We use the algorithm sha256 for encoding according to Meta Pixel documentation
// https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/
// https://developers.google.com/google-ads/api/docs/conversions/enhanced-conversions/web#prepare-data
export const hashUserData = (value: string) => {
  return sha256(value);
};
